import { Dialog } from "@mui/material";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Child } from "../../api/child";
import { useGlobalStore } from "../../api/globalStores";
import { IAvatar } from "../../Common/Avatars/Avatar";
import ButtonPrimary from "../../Common/Buttons/ButtonPrimary";
import BaseCard from "../../Common/Cards/BaseCard";
import CustomScrollbars from "../../Common/Misc/CustomScrollBars";
import Modal from "../../Common/Utility/Modal";
import AvatarNameListItem from "./AvatarNameListItem";
import { getEventDay, getEventMonth } from "../../util/getEventMonth";
import { toJS } from "mobx";
import { TimeSlotDialog } from "./Calendar/TimeSlotDialog";
import Table from "./Table/Table";
import IconSearch from "../../Common/Icons/IconSearch";
import IconFilter from "../../Common/Icons/IconFilter";
import { Filters } from "react-table";

export interface IEventInfoCard {
  handleClose: any;
  timeSlotId: number | undefined;
}

export default observer(EventInfoCard);

function EventInfoCard(props: IEventInfoCard) {
  const globalStores = useGlobalStore();

  const [openUpdateTimeSlot, setOpenUpdateTimeSlot] = useState(false);
  const [openNewStudentDialog, setOpenNewStudentDialog] = useState(false);
  const [allStudents, setAllStudents] = useState<Child[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    globalStores.curTalkingParty.pullDetails();
    Child.getAllChilds().then((allChilds) => setAllStudents(allChilds));
  }, []);

  const startTime = new Date(
    globalStores.curTalkingParty.talkingParty.startTime!
  );

  const eventDay = getEventDay(startTime);
  const eventMonth = getEventMonth(startTime);

  const beginClass = () => {
    navigate("/teacher/lesson");
  };

  return (
    <>
      <TimeSlotDialog
        timeSlotId={props.timeSlotId}
        open={openUpdateTimeSlot}
        onClose={() => setOpenUpdateTimeSlot(false)}
      />
      <Modal
        open={openNewStudentDialog}
        onClose={() => {
          setOpenNewStudentDialog(false);
        }}
      >
        <AddStudentToClass
          students={allStudents}
          onClose={() => {
            setOpenNewStudentDialog(false);
          }}
        />
      </Modal>
      <div className="inline-flex">
        <div className="p-8 relative gap-2.5 bg-white inline-flex flex-col items-start justify-start rounded-[20px] w-[378px] drop-shadow-lg overflow-clip">
          <div className="font-poppins text-xlarge font-medium text-brij-Mud inline leading-[1.4]">
            Class {globalStores.curClass.class.id} : Level{" "}
            {globalStores.curClass.level.hskDifficulty}
          </div>
          <p
            className="font-poppins text-normal text-gray-600 inline leading-[1.5] cursor-pointer"
            onClick={() => {
              setOpenUpdateTimeSlot(true);
            }}
          >
            Update Time Slot
          </p>
          <div className="h-6 left-8 w-[314px] top-[70px]">
            <p className="font-poppins text-normal font-medium text-brij-Black inline leading-[1.5]">
              Module:&nbsp;
            </p>
            <p className="font-poppins text-normal text-brij-Black inline leading-[1.5]">
              {/* TODO: Fix module display */}
              {globalStores.curTalkingParty.module?.name ??
                "Module not set yet"}
            </p>
          </div>
          <div className="justify-between w-full flex">
            <p className="font-poppins text-normal text-black inline leading-[1.5]">
              {eventDay}, {eventMonth} {startTime?.getDate()} /{" "}
              {(startTime.getHours() ?? (0 as number))
                .toString()
                .padStart(2, "0")}
              :
              {(startTime.getMinutes() ?? (0 as number))
                .toString()
                .padStart(2, "0")}{" "}
              –{" "}
              {(((startTime.getHours() ?? 0) + 1) as number)
                .toString()
                .padStart(2, "0")}
              :
              {(startTime.getMinutes() ?? (0 as number))
                .toString()
                .padStart(2, "0")}
            </p>
          </div>
          <div className="flex w-full">
            <div className="font-poppins text-normal text-black inline leading-[1.5] grow whitespace-nowrap">
              {globalStores.curClass.childAccounts.length} student(s)
            </div>

            <div
              className="font-poppins text-normal text-gray-600 inline leading-[1.5] shrink cursor-pointer"
              onClick={() => {
                setOpenNewStudentDialog(true);
              }}
            >
              + Add Student
            </div>
          </div>
          <div className="inline-flex w-full">
            <StudentsList
              students={(globalStores.curClass.childs ?? []).map((child) => ({
                child,
              }))}
            />
          </div>
          <ButtonPrimary onClick={beginClass}>Begin Class</ButtonPrimary>
        </div>
      </div>
    </>
  );
}

interface StudentsListProps {
  onClick?: (child: Child) => void;
  students: {
    child: Child;
    selected?: boolean;
  }[];
}

interface AddStudentToClassProps {
  students: Child[];
  onClose: () => void;
}

//TODO: Flesh out this interface
const AddStudentToClass = observer((props: AddStudentToClassProps) => {
  const globalStores = useGlobalStore();
  const [selectedStudent, setSelectedStudent] = useState<number | undefined>();

  const addStudentToClass = async () => {
    if (!selectedStudent) {
      return toast.error("Please select a student first!");
    }

    await globalStores.curClass.addStudent(selectedStudent);
    props.onClose();
  };

  const rowData = props.students.map((child) => {
    const selected = child?.childAccount.id === selectedStudent;
    return {
      child,
      selected,
    };
  });

  return (
    <BaseCard>
      <div className="flex flex-col gap-3 w-full max-h-[70vh]">
        <div className="flex-1 overflow-auto min-h-0">
          <Table
            rowData={rowData}
            colData={[
              {
                id: "selected",
                Header: "Selected",
                accessor: (row: { child: Child; selected: boolean }) => {
                  return row.selected ? "✅" : "❌";
                },
              },
              {
                Header: "Name",
                accessor: (row: { child: Child; selected: boolean }) => {
                  return (
                    row.child.userDetail.firstName +
                    " " +
                    row.child.userDetail.lastName
                  );
                },
              },
            ]}
            onClickRow={(row) => {
              setSelectedStudent(row.child.childAccount.id);
            }}
          />
        </div>

        <div className="flex-shrink-0 sticky bottom-0 bg-white pt-2">
          <ButtonPrimary
            onClick={() => {
              toast.promise(addStudentToClass(), {
                success: "Student added successfully!",
                error: (e) => e.message,
                loading: "Adding student to class",
              });
            }}
          >
            Add Student
          </ButtonPrimary>
        </div>
      </div>
    </BaseCard>
  );
});

function StudentsList(props: StudentsListProps) {
  return (
    <div className="p-2.5 gap-2.5 bg-white inline-flex flex-col items-start justify-start h-[199px] w-full">
      <CustomScrollbars>
        {props.students.map((student) => {
          return (
            <AvatarNameListItem
              key={student.child.childAccount.id}
              firstName={student.child.userDetail.firstName ?? ""}
              lastName={student.child.userDetail.lastName ?? ""}
              icon={Child.getIcon(student.child)}
              active={Boolean(student.selected)}
              onClick={() => props.onClick?.(student.child)}
            />
          );
        })}
      </CustomScrollbars>
    </div>
  );
}
